/**
 * CashCard (Patch BL): list of labeled cash holdings. Each entry has a
 * label and a USD amount. Simple shape, no math beyond a per-card total
 * shown at the bottom.
 *
 * Multiple entries vs single global number: by design. Users typically
 * have cash spread across accounts (checking, brokerage cash, money
 * market) and the list view makes that visible at a glance instead of
 * collapsing into one less-informative number.
 */

import { useState } from 'react';
import { useAppStore } from '../state/store.js';
import { Decimal, ZERO } from '../engine/money.js';
import { formatUsd } from './Money.js';
import { DecimalInput } from './DecimalInput.js';

export function CashCard(): JSX.Element {
  const entries = useAppStore(s => s.cashEntries);
  const addCashEntry = useAppStore(s => s.addCashEntry);
  const updateCashEntry = useAppStore(s => s.updateCashEntry);
  const removeCashEntry = useAppStore(s => s.removeCashEntry);

  // Local add-row state. Submits to the store on click; resets after.
  const [newLabel, setNewLabel] = useState('');
  const [newAmount, setNewAmount] = useState('');

  // Sum of all entries for the total tile. Empty list = $0.
  const total = entries.reduce((acc, e) => acc.plus(e.amountUsd), ZERO);

  function addRow(): void {
    const label = newLabel.trim();
    const amt = parseFloat(newAmount);
    if (label === '' || !isFinite(amt)) {
      alert('Enter a label and amount.');
      return;
    }
    addCashEntry({ label, amountUsd: new Decimal(amt) });
    setNewLabel('');
    setNewAmount('');
  }

  return (
    <div>
      <p className="muted" style={{ fontSize: 'var(--fs-small)', lineHeight: 1.6, marginTop: 0, marginBottom: 'var(--gap-m)' }}>
        Add cash holdings by account so the Dashboard&apos;s Net worth
        picture includes liquidity. Update balances manually as needed.
      </p>

      {entries.length === 0 ? (
        <p className="muted" style={{ fontStyle: 'italic', marginBottom: 'var(--gap-m)' }}>
          No cash entries yet. Add one below.
        </p>
      ) : (
        <table className="tbl" style={{ marginBottom: 'var(--gap-m)' }}>
          <thead>
            <tr>
              <th>Label</th>
              <th className="num">Amount</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {entries.map(e => (
              <tr key={e.id}>
                <td>
                  <input
                    type="text"
                    value={e.label}
                    onChange={ev => updateCashEntry(e.id, { label: ev.target.value })}
                    style={{ width: 200, fontSize: 'var(--fs-small)' }}
                  />
                </td>
                <td className="num">
                  <DecimalInput
                    decimals={2}
                    step="100"
                    value={e.amountUsd}
                    onChange={d => updateCashEntry(e.id, { amountUsd: d })}
                    style={{ width: 120 }}
                  />
                </td>
                <td>
                  <button
                    className="ghost"
                    onClick={() => removeCashEntry(e.id)}
                    style={{ padding: '2px 6px', fontSize: 'var(--fs-micro)', color: 'var(--text-soft)' }}
                    title="Delete this entry"
                  >
                    del
                  </button>
                </td>
              </tr>
            ))}
            <tr>
              <td style={{ fontWeight: 600 }}>Total</td>
              <td className="num mono" style={{ fontWeight: 600, color: 'var(--accent)' }}>
                ${formatUsd(total, { decimals: 0 })}
              </td>
              <td></td>
            </tr>
          </tbody>
        </table>
      )}

      {/* Add-row form. Always visible at the bottom. */}
      <div className="row" style={{ alignItems: 'center', gap: 'var(--gap-s)', flexWrap: 'wrap' }}>
        <span className="faint mono" style={{ fontSize: 'var(--fs-small)' }}>Add:</span>
        <input
          type="text"
          placeholder="e.g. Chase checking"
          value={newLabel}
          onChange={e => setNewLabel(e.target.value)}
          style={{ width: 200, fontSize: 'var(--fs-small)' }}
        />
        <input
          type="number"
          step="100"
          placeholder="amount USD"
          value={newAmount}
          onChange={e => setNewAmount(e.target.value)}
          style={{ width: 130, fontSize: 'var(--fs-small)' }}
        />
        <button onClick={addRow} style={{ fontSize: 'var(--fs-small)' }}>
          Add entry
        </button>
      </div>
    </div>
  );
}
